/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 30/03/22
 */

import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import get from 'lodash/get';
import { cloneDeep } from 'lodash';

/**
 * Recursive type for nested objects, with optional additional fields
 */
type Recursive<T = object> = T extends object
    ? T & {
          [key: string | number | symbol]: Recursive<T> | unknown;
      }
    : {
          [key: string | number | symbol]: Recursive | unknown;
      };

/**
 * Recursive object, with each layer optionally having a $ref property
 */
type RecursiveWithRef = Recursive<Pick<JsonSchema, '$ref'>>;

/**
 * Get property from nested object, following the specified path
 * @param root Object to get property from
 * @param path Path separated by '/'
 */
const getPath = (root: Recursive, path: string) => {
    const pathSegments = path.split('/');
    if (pathSegments[0] !== '#') {
        throw 'Path must start from root (#)';
    }
    return get(root, pathSegments.slice(1));
};

/**
 * Recursively replace $refs within a nested schema
 * @param rootSchema Root schema to load $ref paths from
 * @param schema Schema to replace $refs within
 */
const replaceInnerInlineRefs = (rootSchema: RecursiveWithRef, schema: RecursiveWithRef) => {
    if (schema.$ref) {
        const foundSchema = getPath(rootSchema, schema.$ref);
        if (foundSchema && typeof foundSchema === 'object') {
            delete schema.$ref;
            Object.assign(schema, foundSchema);
        }
    }
    Object.values(schema).forEach((v) => {
        if (v && typeof v === 'object') {
            replaceInnerInlineRefs(rootSchema, v as typeof schema);
        }
    });
};

/**
 * Replace inline $refs within a JsonSchema or UISchema
 * @param schema Schema to replace refs in
 */
const replaceInlineRefs = <T extends JsonSchema | UISchemaElement>(schema: T): T => {
    const newSchema = cloneDeep(schema as unknown as RecursiveWithRef);
    replaceInnerInlineRefs(newSchema, newSchema);
    return newSchema as unknown as T;
};

class InlineRefParser {
    static expandRefs = replaceInlineRefs;
}

export default InlineRefParser;
